import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "@/lib/motion";
import { cn } from "@/lib/utils";
import { ChevronDown, Search, X, MapPin } from "@/components/icons";
import { CITY_DATA } from "@/components/sections/shared/globe";

export function CountryDropdown({
  selectedIdx,
  onSelect,
}: {
  selectedIdx: number;
  onSelect: (idx: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIdx, setHighlightedIdx] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setSearch("");
      return;
    }
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    const handler = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", handler);
    };
  }, [open]);

  useEffect(() => {
    setHighlightedIdx(0);
  }, [search, open]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const filtered = CITY_DATA.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));
  const selected = selectedIdx >= 0 ? CITY_DATA[selectedIdx] : null;

  const selectHighlighted = () => {
    const city = filtered[highlightedIdx];
    if (!city) return;
    onSelect(CITY_DATA.indexOf(city));
    setOpen(false);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      selectHighlighted();
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          "3xl:py-3.5 flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-2.5 text-sm font-medium transition-all duration-200",
          open ? "border-brand/60 bg-card shadow-sm" : "border-border bg-card hover:border-brand/40"
        )}
      >
        {selected ? (
          <span className="flex min-w-0 items-center gap-2">
            <span className="shrink-0 text-base">{selected.flag}</span>
            <span className="text-foreground truncate">{selected.name}</span>
          </span>
        ) : (
          <span className="text-muted-foreground flex items-center gap-2">
            <MapPin size={14} aria-hidden="true" />
            Select a country to filter stats...
          </span>
        )}
        <span className="flex shrink-0 items-center gap-1.5">
          <ChevronDown
            size={14}
            className={cn(
              "text-muted-foreground transition-transform duration-200",
              open && "rotate-180"
            )}
            aria-hidden="true"
          />
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.14 }}
            id="country-listbox"
            role="listbox"
            aria-label="Country list"
            className="border-border bg-background/95 absolute top-full left-0 z-50 mt-2 w-full overflow-hidden rounded-xl border shadow-2xl backdrop-blur-md"
          >
            <div className="border-border flex items-center gap-2 border-b px-3 py-3.5">
              <Search size={13} className="text-muted-foreground shrink-0" aria-hidden="true" />
              <input
                ref={inputRef}
                type="text"
                role="combobox"
                placeholder="Search countries..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="focus-visible:ring-ring placeholder:text-muted-foreground text-foreground flex-1 bg-transparent px-2 py-1 text-sm outline-none focus-visible:rounded-md focus-visible:ring-2"
                aria-label="Search countries"
                aria-expanded={open}
                aria-controls="country-listbox"
                aria-activedescendant={
                  filtered[highlightedIdx]
                    ? `country-option-${CITY_DATA.indexOf(filtered[highlightedIdx])}`
                    : undefined
                }
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Clear search"
                >
                  <X size={12} />
                </button>
              )}
            </div>

            <div className="max-h-36 overflow-y-auto py-1 sm:max-h-56">
              {filtered.length === 0 ? (
                <p className="text-muted-foreground py-5 text-center text-sm">No countries found</p>
              ) : (
                filtered.map((c, filteredIdx) => {
                  const idx = CITY_DATA.indexOf(c);
                  const isSelected = idx === selectedIdx;
                  const isHighlighted = filteredIdx === highlightedIdx;
                  return (
                    <button
                      key={c.name}
                      id={`country-option-${idx}`}
                      role="option"
                      aria-selected={isSelected}
                      onMouseEnter={() => setHighlightedIdx(filteredIdx)}
                      onClick={() => {
                        onSelect(idx);
                        setOpen(false);
                      }}
                      className={cn(
                        "flex w-full items-center justify-between gap-3 px-3 py-2 text-sm transition-colors",
                        isSelected ? "bg-brand/10 text-brand" : "hover:bg-muted text-foreground",
                        isHighlighted && !isSelected && "bg-muted"
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-2.5">
                        <span className="w-5 shrink-0 text-base">{c.flag}</span>
                        <span className="truncate font-medium">{c.name}</span>
                      </span>
                      <span
                        className={cn(
                          "shrink-0 text-xs font-semibold",
                          isSelected ? "text-brand/80" : "text-muted-foreground"
                        )}
                      >
                        {c.ips}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function StatValue({ value, statsKey }: { value: string; statsKey: string }) {
  const prefersReducedMotion = useReducedMotion();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={statsKey + value}
        initial={prefersReducedMotion ? false : { opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.22 }}
        className="text-brand-green mb-0.5 text-2xl font-bold tabular-nums"
      >
        {value}
      </motion.div>
    </AnimatePresence>
  );
}
